import '@common/modules/workspaces/host'

import { WorkspaceService } from '@graft/nuget-gc.webportals.workspaces'
import type { StateCreator } from 'zustand'

import { getSessionToken } from '@common/core/helpers'

import { organizationToObject } from '../../objects'
import type { OrganizationsStore } from '../types'

import type { OrganizationsActionsSlice } from './organizations-actions.slice.type'

export const createOrganizationsActionsSlice: StateCreator<
  OrganizationsStore,
  [['zustand/immer', never]],
  [],
  OrganizationsActionsSlice
> = (set) => ({
  getOrganizations: async () => {
    set((state: OrganizationsStore) => {
      state.loading = true
      state.error = null
    })

    try {
      const token = await getSessionToken()
      const entities = await WorkspaceService.getWorkspacesByUserId(token)
      const organizations = await Promise.all((entities ?? []).map((e) => organizationToObject(e)))
      set((state: OrganizationsStore) => {
        state.organizations = organizations
      })
    } catch {
      set((state: OrganizationsStore) => {
        state.organizations = []
      })
    } finally {
      set((state: OrganizationsStore) => {
        state.loading = false
      })
    }
  },

  createOrganization: async (name: string) => {
    set((state: OrganizationsStore) => {
      state.loading = true
      state.error = null
    })

    try {
      const token = await getSessionToken()
      const organizationId = await WorkspaceService.addWorkspace(token, name)

      const refreshToken = await getSessionToken()
      const entities = await WorkspaceService.getWorkspacesByUserId(refreshToken)
      const organizations = await Promise.all((entities ?? []).map((e) => organizationToObject(e)))

      set((state: OrganizationsStore) => {
        state.organizations = organizations
      })

      return organizationId ?? null
    } catch (err) {
      set((state: OrganizationsStore) => {
        state.error = err instanceof Error ? err.message : 'Failed to create organization'
      })
      return null
    } finally {
      set((state: OrganizationsStore) => {
        state.loading = false
      })
    }
  },

  updateOrganizationName: async (organizationId: string, name: string) => {
    set((state: OrganizationsStore) => {
      state.loading = true
      state.error = null
    })

    try {
      const token = await getSessionToken()
      await WorkspaceService.updateWorkspaceName(token, organizationId, name)
      set((state: OrganizationsStore) => {
        state.organizations = state.organizations.map((o) => (o.id === organizationId ? { ...o, name } : o))
      })
    } catch (err) {
      set((state: OrganizationsStore) => {
        state.error = err instanceof Error ? err.message : 'Failed to update organization'
      })
    } finally {
      set((state: OrganizationsStore) => {
        state.loading = false
      })
    }
  },

  deleteOrganization: async (organizationId: string) => {
    set((state: OrganizationsStore) => {
      state.loading = true
      state.error = null
    })

    try {
      const token = await getSessionToken()
      await WorkspaceService.deleteWorkspace(token, organizationId)
      set((state: OrganizationsStore) => {
        state.organizations = state.organizations.filter((o) => o.id !== organizationId)
      })
    } catch (err) {
      set((state: OrganizationsStore) => {
        state.error = err instanceof Error ? err.message : 'Failed to delete organization'
      })
    } finally {
      set((state: OrganizationsStore) => {
        state.loading = false
      })
    }
  },

  clearError: () => {
    set((state: OrganizationsStore) => {
      state.error = null
    })
  },
})
